import { Box, Typography, Popper, Paper } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { getLotReferenceStyle, getLotReferenceLabel } from '../utils/lotsUtils';

const LotPlanCanvas = ({
  allotment,
  lots,
  projectsMap = {},
  onLotClick,
  onLotDragEnd,
  dragEnabled = false,
}) => {
  const containerRef = useRef(null);
  const [dragState, setDragState] = useState({ lotId: null, x: 0, y: 0, moved: false });
  const lastDragRef = useRef({ lotId: null, ts: 0 });
  const [hoveredLot, setHoveredLot] = useState(null);
  const [hoverAnchor, setHoverAnchor] = useState(null);
  const clampPixel = (value, min, max) => Math.max(min, Math.min(max, value));

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!dragEnabled || !dragState.lotId || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const nextX = clampPixel(event.clientX - rect.left, 0, rect.width);
      const nextY = clampPixel(event.clientY - rect.top, 0, rect.height);
      setDragState((prev) => ({ ...prev, x: nextX, y: nextY, moved: true }));
    };

    const handlePointerUp = (event) => {
      if (!dragEnabled || !dragState.lotId || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const nextX = clampPixel(event.clientX - rect.left, 0, rect.width);
      const nextY = clampPixel(event.clientY - rect.top, 0, rect.height);
      onLotDragEnd?.(
        lots.find((lot) => lot.uuid === dragState.lotId),
        {
          x: Math.round(nextX),
          y: Math.round(nextY),
        }
      );
      lastDragRef.current = { lotId: dragState.lotId, ts: Date.now() };
      setDragState({ lotId: null, x: 0, y: 0, moved: false });
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragState.lotId, onLotDragEnd, lots]);

  const normalizePosition = (value) => {
    if (value == null) return 0;
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return 0;
    return numeric;
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const resolveProject = (lot) => lot?.project || projectsMap[lot?.uuid] || null;
  const isProjectInProgress = (lot) => {
    const project = resolveProject(lot);
    return Boolean(project?.status === 'in_progress' || lot?.project_is_in_progress);
  };
  const isProjectCompleted = (lot) => {
    const project = resolveProject(lot);
    return Boolean(project?.status === 'completed' || lot?.project_is_completed);
  };

  if (!allotment?.image_url) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Seleccioná un loteo con plano cargado para ver el mapa.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        minWidth: '1500px',
        maxWidth: '1500px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'grey.100',
      }}
    >
      <Box
        component="img"
        src={allotment.image_url}
        alt={allotment.name}
        sx={{
          width: '100%',
          height: 'auto',
          objectFit: 'contain',
          display: 'block',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
        }}
      >
        {lots.map((lot) => {
          const isDragging = dragState.lotId === lot.uuid;
          const left = isDragging ? dragState.x : normalizePosition(lot.pos_x);
          const top = isDragging ? dragState.y : normalizePosition(lot.pos_y);
          const blockLabel = (lot.block_number || '').trim();
          const lotLabel = (lot.lot_number || '').trim();
          const labelParts =
            blockLabel && lotLabel ? [blockLabel, lotLabel] : [blockLabel || lotLabel];
          const project = resolveProject(lot);
          const indicatorStyle = getLotReferenceStyle({
            lot,
            project,
            progressSummary: lot?.project_progress_summary,
            qualitySummary: lot?.project_quality_summary,
          });

          const spinnerColor = indicatorStyle.spinnerColor || 'rgba(149, 200, 255, 0.9)';
          const borderWidth = indicatorStyle.spinner ? 4 : 4;

          return (
            <Box
              key={lot.uuid}
              onMouseEnter={(event) => {
                if (dragEnabled) return;
                setHoveredLot(lot);
                setHoverAnchor(event.currentTarget);
              }}
              onMouseLeave={() => {
                setHoveredLot(null);
                setHoverAnchor(null);
              }}
              onPointerDown={(event) => {
                if (!dragEnabled) {
                  return;
                }
                event.stopPropagation();
                setDragState({ lotId: lot.uuid, x: left || 0, y: top || 0, moved: false });
              }}
              onClick={(event) => {
                event.stopPropagation();
                if (dragEnabled) {
                  return;
                }
                const isRecentDrag =
                  lastDragRef.current.lotId === lot.uuid &&
                  Date.now() - lastDragRef.current.ts < 400;
                if (isRecentDrag) {
                  return;
                }
                onLotClick?.(lot);
              }}
              sx={{
                position: 'absolute',
                left: `${left || 0}px`,
                top: `${top || 0}px`,
                transform: 'translate(-50%, -50%)',
                width: { xs: 34, md: 42 },
                height: { xs: 34, md: 42 },
                borderRadius: '50%',
                backgroundColor: isDragging ? 'primary.main' : indicatorStyle.backgroundColor,
                color: isDragging ? 'common.white' : indicatorStyle.color,
                borderStyle: 'solid',
                borderWidth,
                borderColor: isDragging ? 'primary.dark' : indicatorStyle.borderColor,
                borderTopColor: indicatorStyle.spinner
                  ? spinnerColor
                  : isDragging
                    ? 'primary.dark'
                    : indicatorStyle.borderColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: { xs: 10, md: 12 },
                fontWeight: 700,
                lineHeight: 1.1,
                textAlign: 'center',
                cursor: dragEnabled ? 'grab' : 'pointer',
                zIndex: 2,
                pointerEvents: 'auto',
                userSelect: 'none',
                '&::before': indicatorStyle.spinner
                  ? {
                      content: '""',
                      position: 'absolute',
                      inset: -4,
                      borderRadius: '50%',
                      border: `${borderWidth}px solid ${indicatorStyle.borderColor}`,
                      borderTopColor: spinnerColor,
                      '@keyframes lotMarkerSpin': {
                        from: { transform: 'rotate(0deg)' },
                        to: { transform: 'rotate(360deg)' },
                      },
                      animation: 'lotMarkerSpin 1s linear infinite',
                    }
                  : undefined,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {labelParts.map((part, index) => (
                  <Box key={`${lot.uuid}-label-${index}`} component="span">
                    {part}
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Popper
        open={Boolean(hoveredLot && !dragEnabled)}
        anchorEl={hoverAnchor}
        placement="top-start"
        disablePortal
        modifiers={[
          { name: 'offset', options: { offset: [0, 10] } },
          {
            name: 'preventOverflow',
            options: {
              boundary: containerRef.current || 'viewport',
              padding: 16,
              altAxis: true,
              tether: true,
            },
          },
          {
            name: 'shift',
            options: {
              padding: 16,
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['top-end', 'bottom-start', 'bottom-end', 'top', 'bottom'],
            },
          },
        ]}
        sx={{ zIndex: 4, pointerEvents: 'none' }}
      >
        {hoveredLot && (
          <Paper
            elevation={4}
            sx={{
              p: 2,
              minWidth: 280,
              maxWidth: 360,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Estado
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {getLotReferenceLabel({ lot: hoveredLot, project: resolveProject(hoveredLot) })}
                </Typography>
              </Box>

              {(hoveredLot?.contract?.contract_number ||
                ((isProjectInProgress(hoveredLot) || isProjectCompleted(hoveredLot)) &&
                  !hoveredLot?.uuid_contract)) && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Contrato
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={
                      hoveredLot?.uuid_contract || hoveredLot?.contract?.contract_number
                        ? 'text.primary'
                        : 'error.main'
                    }
                  >
                    {hoveredLot?.contract?.contract_number
                      ? `#${hoveredLot.contract.contract_number}`
                      : 'Sin contrato asignado'}
                  </Typography>
                </Box>
              )}

              {resolveProject(hoveredLot)?.uuid && (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Inicio Obra
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDate(resolveProject(hoveredLot)?.start_date)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Fin Obra
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDate(resolveProject(hoveredLot)?.end_date)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Progreso de Obra
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {Number(hoveredLot?.project_progress_summary?.completion_percentage ?? 0)}%
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Calidad de Obra
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {Number(hoveredLot?.project_quality_summary?.completion_percentage ?? 0)}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Contratista
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={
                        resolveProject(hoveredLot)?.subcontractor_name
                          ? 'text.primary'
                          : 'error.main'
                      }
                    >
                      {resolveProject(hoveredLot)?.subcontractor_name || 'Sin contratista asignado'}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        )}
      </Popper>
    </Box>
  );
};

export default LotPlanCanvas;
